import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useSessionStore, labelMap } from './SessionStore';
import { useSiteSettings } from './context/SiteSettingsContext';
import ModalWrapper from './components/Modal/ModalWrapper';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import LabelIcon from '@mui/icons-material/Label';
import MicIcon from '@mui/icons-material/Mic';

import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ArticleIcon from '@mui/icons-material/Article';
import Portal from './components/Portal';
import AudioRecorderPanel from './components/AudioRecorderPanel';
import DimmedOverlay from './components/DimmedOverlay';
import { useDialog } from './components/Modal/DialogContext';
import { Link } from "react-router-dom"; // Stelle sicher, dass NavLink importiert ist
import type { AppMode } from './App';
import { useUser } from "./context/UserContext";
import { getRecordingMaxMinutes } from "./utils/recordingDurationLabel";

interface WakeLockSentinel {
  release: () => Promise<void>;
}

interface WakeLock {
  request: (type: 'screen') => Promise<WakeLockSentinel>;
}

interface AudioRecorderProps {
  isAudioDialogOpen: boolean; // Zustand, ob der Dialog geöffnet ist
  setIsAudioDialogOpen: React.Dispatch<React.SetStateAction<boolean>>; // Funktion zum Ändern des Dialogzustands
}

interface DictateTabProps {
  mode: AppMode;
}

type RecordingState = "inactive" | "recording" | "paused";

export const generateAudioTimestamp = (extension: string = ""): string => {

  return `audio_${new Date().toISOString().replace(/[:.]/g, '-')}${extension}`;
};

export const triggerUploadCompletion = async (session_id: string, filename: string, audioname: string, lastchunkid: number) => {


  try {
    console.log({
      filename,
      audioname,
      lastchunkid,
    });
    const response = await axios.post(`/api/services/complete-upload/`, {
      session_id: session_id, filename: filename, audioname: audioname, lastchunkid: lastchunkid
    });

    console.log('Audio-Processing abgeschlossen:', response.data);
    //alert(`Audio-Datei wurde erfolgreich verarbeitet: ${response.data.final_file}`);
  } catch (error) {
    console.error('Fehler beim Abschließen der Audioverarbeitung:', error);
    alert('Fehler beim Abschließen der Verarbeitung. Details in der Konsole.');
  }
};

const DictateTab: React.FC<DictateTabProps> = ({ mode }) => {
  const isCase = mode === "case";
  void mode;
  const { settings: siteSettings, loading: isSiteSettingsLoading } = useSiteSettings();
  const logLevel = siteSettings?.frontend_log_level ?? 0;
  const { fetchSessionDataById, setSelectedSessionId, selectedSessionId, setIsSessionSelected } = useSessionStore();
  const dialog = useDialog();
  const { user } = useUser();


  const [showTipsDialog, setShowTipsDialog] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [recordingState, setRecordingState] = useState<RecordingState>('inactive'); // Mögliche Werte: 'inactive', 'recording', 'paused'
  const [recordingTime, setRecordingTime] = useState(0); // in Sekunden
  const maxMinutes = getRecordingMaxMinutes(user, 15);
  const hasExceededLimit = recordingTime >= maxMinutes * 60;
  const [audioName, setAudioName] = useState<string>('');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [recoderSessionId, setRecoderSessionId] = useState<string>('');
  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
  const [generatedFilename, setGeneratedFilename] = useState<string>("empty");
  const sessionIdRef = useRef<string>(''); // Ref für session_id
  const chunkIdRef = useRef<number>(0); // ID des Chunks
  const fileNameRef = useRef<string>(''); // ID des Chunks
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingStateRef = useRef<RecordingState>('inactive');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);

  // Für den Autio-Level-Leser
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const volumeBarRef = useRef<HTMLCanvasElement | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const smoothedBarsRef = useRef<number[]>([]);


  const timerRef = useRef<NodeJS.Timeout | null>(null);


  // Für die Audioaufnahem
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      setRecordingState('inactive'); // Stoppe die Loop eindeutig!

      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }

      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);


  const sendHearbeat = async () => {
    try {
      // Optional: Start-Logik für Aufnahme hier

      // 🔄 Heartbeat senden
      const res = await axios.get('/auth/api/heartbeat/');
      if (!isSiteSettingsLoading && logLevel >= 3) console.log("Heartbeat gesendet:", res.data);

      // Optional: Weiter mit Aufnahme-Logik
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error("Heartbeat fehlgeschlagen:", error);
    }
  };


  const startRecording = async () => {
    sendHearbeat(); // Heartbeat senden
    setRecordingTime(0); // Zeit zurücksetzen
    if (timerRef.current) clearInterval(timerRef.current); // Timer zurücksetzen
    timerRef.current = setInterval(() => {
      setRecordingTime((prev) => prev + 1);
    }, 1000);

    // generate session_id and file name with timestamp
    sessionIdRef.current = generateAudioTimestamp();
    setRecoderSessionId(sessionIdRef.current)

    fileNameRef.current = `${sessionIdRef.current}.wav`;
    setGeneratedFilename(fileNameRef.current)

    chunkIdRef.current = 0; // Initialisieren der chunk_id
    const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    setStream(audioStream);
    audioStreamRef.current = audioStream;
    recordingStateRef.current = 'recording';

    startRecordingLoop()
  }



  const startRecordingLoop = async () => {
    try {
      audioChunksRef.current = [];

      // Fallback für Safari: prüfe unterstützte MIME-Typen
      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        // Safari: fallback auf unkomprimiertes WAV (wenn unterstützt)
        mimeType = 'audio/wav';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          alert('Dieser Browser unterstützt kein geeignetes Audioformat für die Aufnahme.');
          return;
        }
      }

      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256; // Anzahl der Samples
      analyserRef.current = analyser;

      const source = audioContext.createMediaStreamSource(audioStreamRef.current!);
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      dataArrayRef.current = dataArray;

      const recorder = new MediaRecorder(audioStreamRef.current!, { mimeType });
      mediaRecorderRef.current = recorder;

      setMediaRecorder(recorder);

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      recorder.onstop = () => {
        let blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        chunkIdRef.current++;
        const isLastChunk = recordingStateRef.current === "inactive";
        if (!isSiteSettingsLoading && logLevel >= 3) console.log('isLastChunk', isLastChunk); // Debugging-Log
        if (!isSiteSettingsLoading && logLevel >= 3) console.log('recordingStateRef.current ', recordingStateRef.current); // Debugging-Log


        sendAudioChunkToServer(blob, fileNameRef.current, sessionIdRef.current, chunkIdRef.current, isLastChunk);
        if (!isSiteSettingsLoading && logLevel >= 3) console.log('RecordingState:', recordingState); // Debugging-Log
        if (!isSiteSettingsLoading && logLevel >= 3) console.log('recordingStateRef.current ', recordingStateRef.current); // Debugging-Log

        if (recordingStateRef.current !== "inactive") {
          startRecordingLoop();
        } else {
          try {
            //triggerUploadCompletion(sessionIdRef.current, fileNameRef.current, audioName, chunkIdRef.current);
          } catch (error) {
            if (!isSiteSettingsLoading && logLevel >= 1) console.error('Fehler beim automatischen Abschluss der Verarbeitung:', error);
            alert('Fehler beim automatischen Abschluss der Verarbeitung. Bitte versuchen Sie es manuell.');
          }
        }
      }

      recorder.start(); // Aufnahme starten
      setTimeout(() => {
        if (recorder.state !== "inactive") {
          recorder.stop();
        }
      }, 600000); // Nach x ms stoppen um Chunks zu senden

      setRecordingState('recording');
      setAudioChunks([]);
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error('Error starting recording:', error);
      alert('Fehler beim Starten der Aufnahme');
    }
  };

  const pauseRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.pause();
      setRecordingState('paused');
      recordingStateRef.current = 'paused';
      if (timerRef.current) {
        clearInterval(timerRef.current); // Timer stoppen
      }
      //alert('Aufnahme pausiert');
    }
  };

  const resumeRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'paused') {
      mediaRecorder.resume();
      setRecordingState('recording');
      recordingStateRef.current = 'recording';
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
      //alert('Aufnahme fortgesetzt');
    }
  };

  const stopRecording = async () => {
    if (mediaRecorder) {
      setRecordingState('inactive');
      recordingStateRef.current = 'inactive';

      mediaRecorder.stop();
      stream?.getTracks().forEach((track) => track.stop());
      setStream(null);
      setMediaRecorder(null);

      if (timerRef.current) {
        clearInterval(timerRef.current); // Timer stoppen
        timerRef.current = null;
      }
      setRecordingTime(0);
      setIsSessionSelected(true);
      // Zeit zurücksetzen
      //alert('Aufnahme gestoppt. Verarbeitung wird automatisch gestartet.');

      // Dialogfeld automatisch schließen
      // Trigger automatisch ausführen
    }
  };

  const abortRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.ondataavailable = null;
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => track.stop());
    }

    setMediaRecorder(null);
    setRecordingState('inactive');
    recordingStateRef.current = 'inactive';

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setRecordingTime(0);
    setStream(null);
    setAudioChunks([]);
  };



  const handleAbort = () => {
    dialog({
      type: 'confirm',
      title: 'Aufnahme abbrechen',
      message: 'Soll die Aufnahme wirklich gelöscht werden?',
      confirmButton: 'Bestätigen',
      cancelButton: 'Abbrechen',

      onConfirm: () => abortRecording(),
    });
  };





  const sendAudioChunkToServer = async (
    chunk: Blob,
    filename: string,
    session_id: string,
    chunk_id: number,
    is_lastchunk: boolean
  ) => {
    const formData = new FormData();
    formData.append('audio', chunk, filename);
    formData.append('filename', filename);
    formData.append('session_id', session_id);
    formData.append('chunk_id', chunk_id.toString());
    formData.append('is_lastchunk', is_lastchunk.toString());
    formData.append('recording_type', "recording");
    formData.append('transcript_type', "report");
    formData.append('entity_type', isCase ? 'case' : 'session');



    try {
      const response = await axios.post(`/api/services/stream-audio/`, formData);
      formData.forEach((value, key) => console.log(key, value));
      if (!isSiteSettingsLoading && logLevel >= 3) console.log('Audio-Chunk erfolgreich gesendet:', response.data);
      if (response.data.session_DB_id) {
        fetchSessionDataById(response.data.session_DB_id);
        setSelectedSessionId(response.data.session_DB_id);
        setIsSessionSelected(true);

      }


    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error('Fehler beim Senden eines Audio-Chunks:', error);
    }
  };

  const isRecording = recordingState !== 'inactive';

  useEffect(() => {
    const container = document.getElementById('tab-content-dictate');
    if (!container) return;
    const prevPosition = container.style.position;
    const prevZIndex = container.style.zIndex;
    const prevBg = container.style.backgroundColor;

    if (isRecording) {
      container.style.position = 'relative';
      container.style.zIndex = '200';
      container.style.backgroundColor = '#fff';
    } else {
      container.style.position = prevPosition;
      container.style.zIndex = prevZIndex;
      container.style.backgroundColor = prevBg;
    }

    return () => {
      container.style.position = prevPosition;
      container.style.zIndex = prevZIndex;
      container.style.backgroundColor = prevBg;
    };
  }, [isRecording]);


  return (
    <div className="tw-text-gray-500 tw-flex-1">
      <DimmedOverlay
        active={isRecording}
        targetId="tab-content-dictate"
        opacity={0.08}
        disableHeader
      />
      <div style={{ display: 'grid', gridTemplateRows: '1fr auto', height: '100%' }}>
        <AudioRecorderPanel
          headerText={isCase ? "Für Berichte" : "Für Sitzungen, die nicht aufgenommen wurden"}
          recordingState={recordingState}
          recordingTime={recordingTime}
          onStart={startRecording}
          onPause={pauseRecording}
          onResume={resumeRecording}
          onStop={stopRecording}
          onAbort={handleAbort}
          startLabel="Diktat starten"
          stopLabel="Diktat beenden"
          deleteLabel="Löschen"
          durationLabel={
            <span>
              Aufnahmedauer: 1–{""}
              <span className={hasExceededLimit ? "tw-text-yellow-500 tw-font-semibold" : ""}>
                {maxMinutes}
              </span>{" "}
              min
            </span>
          }
          limitWarning={
            hasExceededLimit
              ? `Das Zeitlimit von ${maxMinutes} Minuten ist erreicht – Aufnahme läuft weiter.`
              : null
          }
          startIcon={
            <svg
              width="33"
              height="32"
              viewBox="0 0 33 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="tw-w-5"
            >
              <path
                d="M16.513 20.6641C19.4597 20.6641 21.8464 18.2774 21.8464 15.3307V7.9974C21.8464 5.05073 19.4597 2.66406 16.513 2.66406C13.5664 2.66406 11.1797 5.05073 11.1797 7.9974V15.3307C11.1797 18.2774 13.5664 20.6641 16.513 20.6641Z"
                stroke="currentColor"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
              <path
                d="M6.32031 12.8672V15.1339C6.32031 20.7605 10.8936 25.3339 16.5203 25.3339C22.147 25.3339 26.7203 20.7605 26.7203 15.1339V12.8672"
                stroke="currentColor"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
              <path
                d="M14.6641 8.57219C15.8641 8.13219 17.1707 8.13219 18.3707 8.57219"
                stroke="currentColor"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
              <path
                d="M15.4531 11.3978C16.1598 11.2111 16.8931 11.2111 17.5998 11.3978"
                stroke="currentColor"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
              <path
                d="M16.5156 25.3359V29.3359"
                stroke="currentColor"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              ></path>
            </svg>
          }
          startButtonClassName={`_button_19rto_40 _primary_19rto_127 _filled_19rto_80 wiz-card-btn !tw-font-normal${isCase ? " !bg-[#E7A555]" : ""} `}
          stopButtonClassName={`_button_19rto_40 _primary_19rto_127 _filled_19rto_80 wiz-card-btn !tw-font-normal${isCase ? " !bg-[#E7A555]" : ""} `}
          volumeBarRef={volumeBarRef}
          recordingStateRef={recordingStateRef}
          analyserRef={analyserRef}
          dataArrayRef={dataArrayRef}
          overlayClassName="hidden"
          showHeaderOverlay={false}
          headerClassName="tw-mb-1 tw-gap-1 tw-flex tw-flex-row tw-justify-center"
        />
        <div className="tab-tip tw-mt-1">
          <>
            <button
              className="btn-reset tw-underline tw-text-primary-500"
              onClick={() => setShowTipsDialog(true)}
            >
              Tipps zum Diktat
            </button>

            {showTipsDialog && (
              <Portal>

                <ModalWrapper onClose={() => setShowTipsDialog(false)} title="Tipps zum Diktat">
                  <div className="tw-p-4">
                    <ul className="sc-gEvEer fteAEG tw-grid tw-gap-4">
                      <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                            <AssignmentTurnedInIcon fontSize="small" className="tw-max-w-full" />
                          </div>
                          <div>
                            Für das Diktieren ist keine Einwilligung der Klient:innen erforderlich, wenn Sie keine personenbezogenen Daten benennen. Falls Sie eine wünschen, <Link to="/settings/clientconsent" className="sc-eqUAAy iEhnxW underline">laden Sie hier eine Vorlage herunter.</Link>
                          </div>
                        </div>
                      </li>
                      <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                            <MicIcon fontSize="small" className="tw-max-w-full" />
                          </div>
                          <div><span className="tw-font-bold">Für ein optimales Ergebnis vermeiden Sie möglichst Störgeräusche, Pausen und Nebengespräche und achten Sie auf gleichmäßige Lautstärke sowie gut verständliche Stimmen.</span></div>
                        </div>
                      </li>
                      <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                            <MusicNoteIcon fontSize="small" className="tw-max-w-full" />
                          </div>
                          <div>
                            Führen Sie eine Testsitzung durch, um sicherzustellen, dass Ihr Setup stabil ist. {/* <a target="_blank" rel="noreferrer" href="" className="sc-eqUAAy iEhnxW">Mehr erfahren</a> */}
                          </div>
                        </div>
                      </li>
                      <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                            <ChatBubbleOutlineIcon fontSize="small" className="tw-max-w-full" />
                          </div>
                          <div>
                            Eine präzise Benennung von Interventionen und Fachbegriffen hilft LumaNote, die Qualität der Dokumentation zu verbessern.
                          </div>
                        </div>
                      </li>
                      <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                            <LabelIcon fontSize="small" className="tw-max-w-full" />
                          </div>
                          <div>
                            Wenn Sie beim Diktieren die einzelnen Gliederungspunkte der Dokumentationsvorlage ansprechen, kann LumaNote die Inhalte später besser zuordnen und strukturieren. {/* <a target="_blank" rel="noreferrer" href="" className="sc-eqUAAy iEhnxW">Mehr erfahren</a> */}
                          </div>
                        </div>
                      </li>
                      <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                            <PersonAddAltIcon fontSize="small" className="tw-max-w-full" />
                          </div>
                          <div>
                            Das Diktat dient als Zusammenfassung Ihrer Beobachtungen und Gedanken als Therapeut:in.

                          </div>
                        </div>
                      </li>
                      <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                            <PlayCircleOutlineIcon fontSize="small" className="tw-max-w-full" />
                          </div>
                          <div>
                            Bei Pausen wird Ihr Fortschritt gespeichert. Klicken Sie einfach auf „Weiter“.
                          </div>
                        </div>
                      </li>

                      <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                            <InfoOutlinedIcon fontSize="small" className="tw-max-w-full" />
                          </div>
                          <div>
                            Schließen Sie das Diktat über „Diktat beenden“ ab.
                          </div>
                        </div>
                      </li>

                      <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                            <ContentCutIcon fontSize="small" className="tw-max-w-full" />
                          </div>
                          <div>
                            Erstellen Sie bitte für jede Sitzung eine separate Aufnahme.

                          </div>
                        </div>
                      </li>
                      <li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                            <DeleteForeverIcon fontSize="small" className="tw-max-w-full" />
                          </div>
                          <div>
                            Die Diktate werden am Ende des selben Tages automatisch gelöscht.  <a target="_blank" rel="noreferrer" href="https://lumanote.de/security" className="sc-eqUAAy iEhnxW tw-text-primary-500 underline">Mehr erfahren</a>

                          </div>
                        </div>
                      </li>
                      {/*<li className="shadow-none hover:shadow-none bg-transparent hover:bg-transparent">
                        <div className="tw-flex tw-items-center tw-gap-3">
                          <div className="tw-w-5 tw-flex-shrink-0 tw-max-w-full">
                            <ArticleIcon fontSize="small" className="tw-max-w-full" />
                          </div>
                          <div>
                            Die Art der Klient:innen und Anzahl der Sprecher:innen beeinflussen die Vorlagenwahl. <a target="_blank" rel="noreferrer" href="" className="sc-eqUAAy iEhnxW">Mehr erfahren</a>
                          </div>
                        </div>
                      </li>*/}
                    </ul>
                  </div>
                </ModalWrapper>
              </Portal>

            )}
          </>
        </div>
      </div>
    </div>

  );


}

export default DictateTab;
